import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import { 
    viewSubAdminDetails, 
    editSubAdmins, 
    editSubAdminPermissions,
    permissionModuleListings 
} from '../../slice/subAdminSlice'

// Validation Schema for Sub-Admin (password optional for edit)
const subAdminSchema = yup.object().shape({
    email: yup
        .string()
        .required('Email is required')
        .email('Please enter a valid email address'),
    
    password: yup
        .string()
        .nullable()
        .transform((value) => value || null)
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    
    confirm_password: yup
        .string()
        .nullable()
        .when('password', {
            is: (password) => password && password.length > 0,
            then: (schema) => schema
                .required('Please confirm your password')
                .oneOf([yup.ref('password')], 'Passwords must match'),
            otherwise: (schema) => schema.nullable()
        }),
})

const EditSubAdmin = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const { subAdminId } = location.state || {}

    const { subAdminDetail, permissionModuleList, loading } = useSelector(state => state.subAdmin)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [activeTab, setActiveTab] = useState('account') // 'account' or 'permissions'
    
    // Permission state - track each module's permissions
    const [permissions, setPermissions] = useState({})

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(subAdminSchema),
        defaultValues: {
            email: '',
            password: '',
            confirm_password: '',
        }
    })

    // Fetch sub-admin details and permission modules on mount
    useEffect(() => {
        if (subAdminId) {
            dispatch(viewSubAdminDetails({ id: subAdminId }))
            dispatch(permissionModuleListings())
        } else {
            navigate('/subadmins')
        }
    }, [dispatch, subAdminId, navigate])

    // Populate form when sub-admin details are loaded
    useEffect(() => {
        if (subAdminDetail && subAdminDetail.email) {
            setValue('email', subAdminDetail.email)
        }
    }, [subAdminDetail, setValue])

    // Initialize permissions when modules and sub-admin details are loaded
    useEffect(() => {
        if (permissionModuleList && permissionModuleList.length > 0 && subAdminDetail) {
            const initialPermissions = permissionModuleList.reduce((acc, module) => {
                // Find existing permission for this module
                const existingPerm = subAdminDetail.permission?.find(
                    p => p.permission_module_id === module.id
                )

                acc[module.id] = existingPerm ? {
                    id: existingPerm.id, // Keep existing permission ID for updates
                    permission_module_id: module.id,
                    is_view: existingPerm.is_view || 0,
                    is_add: existingPerm.is_add || 0,
                    is_edit: existingPerm.is_edit || 0,
                    is_delete: existingPerm.is_delete || 0,
                    is_status: existingPerm.is_status || 0,
                } : {
                    permission_module_id: module.id,
                    is_view: 0,
                    is_add: 0,
                    is_edit: 0,
                    is_delete: 0,
                    is_status: 0,
                }
                return acc
            }, {})
            setPermissions(initialPermissions)
        }
    }, [permissionModuleList, subAdminDetail])

    // Handle account info update
    const onSubmitAccount = async (data) => {
        setIsSubmitting(true)
      
        try {
            const payload = {
                id: subAdminId,
                email: data.email,
            }

            // Only include password if user entered a new one
            if (data.password && data.password.trim() !== '') {
                payload.password = data.password
            }
      
            const response = await dispatch(editSubAdmins(payload))
      
            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response?.payload?.message || 'Sub-admin updated successfully!',
                    timer: 2000,
                    showConfirmButton: false,
                })

                // Refresh sub-admin details
                dispatch(viewSubAdminDetails({ id: subAdminId }))
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to update sub-admin',
                })
            }
        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    // Handle permission change for a specific module
    const handlePermissionChange = (moduleId, permissionType) => {
        setPermissions(prev => {
            const currentValue = prev[moduleId][permissionType]
            const newValue = currentValue === 1 ? 0 : 1

            // If enabling any permission other than view, also enable view
            const shouldEnableView = newValue === 1 && permissionType !== 'is_view'

            // If disabling view, also disable all other permissions
            const shouldDisableAll = newValue === 0 && permissionType === 'is_view'

            return {
                ...prev,
                [moduleId]: {
                    ...prev[moduleId],
                    [permissionType]: newValue,
                    ...(shouldEnableView && { is_view: 1 }),
                    ...(shouldDisableAll && {
                        is_add: 0,
                        is_edit: 0,
                        is_delete: 0,
                        is_status: 0
                    })
                }
            }
        })
    }

    // Handle "Select All" for a module
    const handleSelectAll = (moduleId) => {
        const currentModule = permissions[moduleId]
        const allSelected = currentModule.is_view === 1 && 
                          currentModule.is_add === 1 && 
                          currentModule.is_edit === 1 && 
                          currentModule.is_delete === 1 && 
                          currentModule.is_status === 1

        setPermissions(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                is_view: allSelected ? 0 : 1,
                is_add: allSelected ? 0 : 1,
                is_edit: allSelected ? 0 : 1,
                is_delete: allSelected ? 0 : 1,
                is_status: allSelected ? 0 : 1,
            }
        }))
    }

    // Submit permissions
    const onSubmitPermissions = async () => {
        setIsSubmitting(true)

        try {
            // Convert permissions object to array format
            const permissionsArray = Object.values(permissions).map(perm => {
                // Remove the 'id' field before sending to API
                const { id, ...permData } = perm
                return permData
            })

            const payload = {
                subadmin_id: subAdminId,
                permissions: permissionsArray
            }

            const response = await dispatch(editSubAdminPermissions(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Permissions updated successfully!',
                    timer: 2000,
                    showConfirmButton: false,
                })

                // Refresh sub-admin details
                dispatch(viewSubAdminDetails({ id: subAdminId }))
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to update permissions',
                })
            }
        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loading && !subAdminDetail?.email) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (!subAdminDetail) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="alert alert-warning">Sub-Admin not found</div>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Edit Sub-Admin</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/subadmins')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Tabs */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-0">
                    <ul className="nav nav-tabs" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                                onClick={() => setActiveTab('account')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'account' ? '2px solid #0d6efd' : '2px solid transparent',
                                    color: activeTab === 'account' ? '#0d6efd' : '#6c757d',
                                    fontWeight: '500',
                                    padding: '12px 24px'
                                }}
                            >
                                Account Information
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
                                onClick={() => setActiveTab('permissions')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'permissions' ? '2px solid #0d6efd' : '2px solid transparent',
                                    color: activeTab === 'permissions' ? '#0d6efd' : '#6c757d',
                                    fontWeight: '500',
                                    padding: '12px 24px'
                                }}
                            >
                                Manage Permissions
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Account Information Tab */}
            {activeTab === 'account' && (
                <form onSubmit={handleSubmit(onSubmitAccount)}>
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Account Information
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-3">
                                {/* Email */}
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Email <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        placeholder="Enter email address"
                                        {...register('email')}
                                    />
                                    {errors.email && (
                                        <div className="invalid-feedback">{errors.email.message}</div>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="col-md-6">
                                    <label className="form-label">
                                        New Password <span className="text-muted">(Leave blank to keep current)</span>
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                        placeholder="Enter new password"
                                        {...register('password')}
                                    />
                                    {errors.password && (
                                        <div className="invalid-feedback">{errors.password.message}</div>
                                    )}
                                    <small className="text-muted">
                                        Password must contain uppercase, lowercase, number, and special character
                                    </small>
                                </div>

                                {/* Confirm Password */}
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Confirm New Password
                                    </label>
                                    <input
                                        type="password"
                                        className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                        placeholder="Confirm new password"
                                        {...register('confirm_password')}
                                    />
                                    {errors.confirm_password && (
                                        <div className="invalid-feedback">{errors.confirm_password.message}</div>
                                    )}
                                </div>

                                {/* Additional Info */}
                                <div className="col-md-12">
                                    <div className="alert alert-info mb-0">
                                        <small>
                                            <strong>Note:</strong> Only fill in the password fields if you want to change the password. 
                                            Leave them blank to keep the current password.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="d-flex gap-3 justify-content-end mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/subadmins')}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                minWidth: '150px'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                'Update Account'
                            )}
                        </button>
                    </div>
                </form>
            )}

            {/* Permissions Tab */}
            {activeTab === 'permissions' && (
                <div>
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex justify-content-between align-items-center" style={{ borderRadius: '8px 8px 0 0' }}>
                            <span>Manage Permissions</span>
                            <small className="text-muted">Select permissions for each module</small>
                        </div>
                        <div className="card-body p-4">
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '25%' }}>Module Name</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>View</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Add</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Edit</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Delete</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Status</th>
                                            <th style={{ width: '15%', textAlign: 'center' }}>Select All</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {permissionModuleList && permissionModuleList.length > 0 ? (
                                            permissionModuleList.map((module) => {
                                                const modulePerms = permissions[module.id]
                                                if (!modulePerms) return null

                                                const allSelected = modulePerms.is_view === 1 && 
                                                                  modulePerms.is_add === 1 && 
                                                                  modulePerms.is_edit === 1 && 
                                                                  modulePerms.is_delete === 1 && 
                                                                  modulePerms.is_status === 1

                                                return (
                                                    <tr key={module.id}>
                                                        <td className="fw-semibold">{module.display_name}</td>
                                                        
                                                        {/* View */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_view === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_view')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Add */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_add === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_add')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Edit */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_edit === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_edit')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Delete */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_delete === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_delete')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Status */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={modulePerms.is_status === 1}
                                                                onChange={() => handlePermissionChange(module.id, 'is_status')}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </td>

                                                        {/* Select All */}
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${allSelected ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                onClick={() => handleSelectAll(module.id)}
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                {allSelected ? 'Deselect All' : 'Select All'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4 text-muted">
                                                    No permission modules available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Permission Summary */}
                            <div className="mt-4 p-3 bg-light rounded">
                                <small className="text-muted">
                                    <strong>Note:</strong> The sub-admin will only be able to access modules and perform actions based on the permissions selected above.
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="d-flex gap-3 justify-content-end mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/subadmins')}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={onSubmitPermissions}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 24px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                minWidth: '180px'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                'Update Permissions'
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default EditSubAdmin;